import React, { FC, useCallback, useMemo } from "react";
import { generatePath, useNavigate, useParams } from "react-router";
import { Country, tldByCountry } from "@lookiero/sty-psp-locale";
import { Layout } from "@lookiero/sty-psp-ui";
import { Order as OrderEntity, OrderStatus, ServiceStatus } from "../../../../domain/entities/order/Order";
import { ItemStatus, Product } from "../../../../domain/entities/product/Product";
import { useCountryFormattedOrderDate } from "../../behaviors/useCountryFormattedOrderDate";
import { useUserShippingAddress } from "../../behaviors/useUserShippingAddress";
import { useViewOrderById } from "../../behaviors/useViewOrderById";
import { useViewQuestionsFromOrderProducts } from "../../behaviors/useViewQuestionsFromOrderProducts";
import { Order as OrderTemplate } from "../../components/templates/order/Order";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { Routes } from "../../routing/routes";

interface ReturnStatus {
  readonly RETURNABLE: boolean;
  readonly IN_PROGRESS: boolean;
  readonly ANY_RETURNED: boolean;
}

const useItemReturnStatus = (order: OrderEntity | undefined) => {
  const status = useMemo(
    () =>
      order?.items?.reduce<ReturnStatus>(
        (acc, currentItem) => {
          const { RETURNABLE, IN_PROGRESS, ANY_RETURNED } = acc;

          return {
            RETURNABLE: RETURNABLE || currentItem.itemStatus === ItemStatus.SELECTED,
            IN_PROGRESS: IN_PROGRESS || currentItem.itemStatus === ItemStatus.TO_RETURN,
            ANY_RETURNED: ANY_RETURNED || currentItem.itemStatus === ItemStatus.RETURN_DONE,
          };
        },
        {
          RETURNABLE: false,
          IN_PROGRESS: false,
          ANY_RETURNED: false,
        },
      ),
    [order?.items],
  );

  return status;
};

interface OrderProps {
  readonly layout: Layout;
  readonly onShowReturnInstructions: (serviceId: string) => void;
}

const Order: FC<OrderProps> = ({ layout, onShowReturnInstructions }) => {
  const {
    session: {
      user: { country: countryCode },
    },
    tradename,
    basePath,
  } = useStaticInfo();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const order = useViewOrderById(orderId as string);
  const faqUrl = useMemo(() => {
    const domain = countryCode === Country.GB ? "co.uk" : tldByCountry(countryCode);

    return `https://${tradename.toLowerCase()}.${domain}/help-and-contact`;
  }, [countryCode, tradename]);

  const returnPrice = order ? Number(order.returnedItemsPrice) + Number(order.pendingReturnItemsPrice) : 0;
  const unavailableItemsPrice = order && order.discardedItemsPrice ? order.discardedItemsPrice : 0;
  const originalPriceAmount = useMemo(() => {
    if (!order) return 0;
    const { promo, initialPrice, discardedItemsPrice, returnedItemsPrice, pendingReturnItemsPrice, orginalTotalPrice } =
      order;

    const hasAmountToDiscountWithPromo =
      promo && (Boolean(discardedItemsPrice) || Boolean(returnedItemsPrice) || Boolean(pendingReturnItemsPrice));
    return (hasAmountToDiscountWithPromo ? initialPrice : orginalTotalPrice) as number;
  }, [order]);
  const promoAmount = useMemo(() => {
    const discountAmount = order ? order.discountAmount : 0;
    return returnPrice || unavailableItemsPrice ? 0 : discountAmount;
  }, [order, returnPrice, unavailableItemsPrice]);

  const userShippingAddress = useUserShippingAddress();
  const countryFormattedDate = useCountryFormattedOrderDate(order, countryCode);
  const questionsPerProduct = useViewQuestionsFromOrderProducts(order);
  const itemReturnStatus = useItemReturnStatus(order);

  const canReturn = Boolean(
    order?.orderStatus === OrderStatus.IN_PROGRESS &&
    [ServiceStatus.SENT, ServiceStatus.DELIVERY_NOTIFIED].includes(order.serviceStatus as ServiceStatus) &&
    itemReturnStatus?.RETURNABLE &&
    !itemReturnStatus?.IN_PROGRESS,
  );

  const showReturnInstructions = Boolean(!itemReturnStatus?.ANY_RETURNED && itemReturnStatus?.IN_PROGRESS);
  const handleOnShowReturnInstructions = useCallback(() => {
    if (order?.serviceId) {
      onShowReturnInstructions(order.serviceId);
    }
  }, [onShowReturnInstructions, order?.serviceId]);

  const handleOnPressOrderProductImage = useCallback(
    (product: Product) =>
      navigate(generatePath(`${basePath}/${Routes.PRODUCT_DETAIL}`, { variantId: product.variantId }), {
        state: { innerNavigation: true },
      }),
    [basePath, navigate],
  );

  const handleOnFinishReturn = useCallback(
    () => navigate(generatePath(`${basePath}/${Routes.RETURN_CONFIRMATION}`, { orderId: orderId as string })),
    [basePath, navigate, orderId],
  );

  const handleOnBack = useCallback(() => navigate(-1), [navigate]);

  return (
    <OrderTemplate
      date={countryFormattedDate}
      faqUrl={faqUrl}
      isCancelled={order?.orderStatus === OrderStatus.CANCELLED}
      layout={layout}
      order={order}
      originalPriceAmount={originalPriceAmount}
      promoAmount={promoAmount}
      questions={questionsPerProduct}
      returnPrice={returnPrice}
      shippingAddress={userShippingAddress}
      showExpired={order?.orderStatus === OrderStatus.CLOSED}
      showRefundNotification={Boolean(itemReturnStatus?.ANY_RETURNED)}
      showReturnButton={canReturn}
      showReturnInstructions={showReturnInstructions}
      unavailableItemsPrice={unavailableItemsPrice}
      onBack={handleOnBack}
      onFinishReturn={handleOnFinishReturn}
      onPressOrderProductImage={handleOnPressOrderProductImage}
      onPressReturnInstructions={handleOnShowReturnInstructions}
    />
  );
};

export { Order };
